import { useState, useEffect, useRef } from "react";
import { IoClose, IoSend } from "react-icons/io5";
import { BsCameraFill } from "react-icons/bs";
import { MdInsertEmoticon } from "react-icons/md";
import Modal, { ModalContent } from "../../elements/Modal";
import { format } from "date-fns";
import toast from "react-hot-toast";
import axios from "axios";

interface ChatMessage {
  message: string;
  customerId?: string;
  adminId?: string;
  _id: string;
  createdAt: string;
}

interface OrderSupportChatModalProps {
  orderId: string;
  orderNumber: string;
  onClose: () => void;
}

const OrderSupportChatModal = ({
  orderId,
  orderNumber,
  onClose,
}: OrderSupportChatModalProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Get admin ID from session storage
  const getAdminId = () => {
    try {
      const auth = sessionStorage.getItem("authState");

      if (auth) {
        const jsonData = JSON.parse(auth);

        // Try multiple possible locations for the admin ID
        const adminId =
          jsonData?.uid || // Primary location for admin ID
          jsonData?.user?._id ||
          jsonData?.user?.id ||
          jsonData?._id ||
          jsonData?.id ||
          jsonData?.adminId ||
          null;

        console.log("Admin ID found:", adminId);
        return adminId;
      }
    } catch (error) {
      console.error("Error getting admin ID:", error);
    }
    return null;
  };

  // Get auth token
  const getAuthToken = () => {
    try {
      const auth = sessionStorage.getItem("authState");
      if (auth) {
        const jsonData = JSON.parse(auth);
        return jsonData?.token || null;
      }
    } catch (error) {
      console.error("Error getting auth token:", error);
    }
    return null;
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch chat messages
  const fetchMessages = async () => {
    setIsLoading(true);
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${process.env.REACT_APP_RESTAURANT_URL}/order/${orderId}/chats`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );

      if (response.data.success) {
        setMessages(response.data.data || []);
      }
    } catch (error: any) {
      console.error("Error fetching messages:", error);
      toast.error(
        error.response?.data?.message || "Failed to load chat messages"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    const adminId = getAdminId();
    if (!adminId) {
      toast.error("Unable to identify admin user");
      return;
    }

    setIsSending(true);
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${process.env.REACT_APP_RESTAURANT_URL}/order/${orderId}/chat`,
        {
          message: newMessage.trim(),
          adminId: adminId,
        },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        // Add the new message to the list
        const newMsg: ChatMessage = {
          message: newMessage.trim(),
          adminId: adminId,
          _id: response.data.data?._id || Date.now().toString(),
          createdAt: new Date().toISOString(),
        };
        setMessages([...messages, newMsg]);
        setNewMessage("");
        toast.success("Message sent");
      }
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast.error(error.response?.data?.message || "Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Format timestamp
  const formatTime = (dateString: string) => {
    try {
      return format(new Date(dateString), "hh:mm a");
    } catch {
      return "";
    }
  };

  useEffect(() => {
    fetchMessages();
    // Poll for new messages every 10 seconds
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [orderId]);

  return (
    <Modal
      open={true}
      onClose={onClose}
      className="flex items-center justify-center"
    >
      <ModalContent className="mx-3 rounded-lg shadow-lg flex flex-col bg-white w-[90%] md:w-[500px] h-[550px] font-mont">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold">Support</h2>
            <p className="text-sm text-gray-500">Order #{orderNumber}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <IoClose className="w-6 h-6" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500 text-center">
                No messages yet.
                <br />
                Start a conversation!
              </p>
            </div>
          ) : (
            messages.map((msg) => {
              const isAdmin = Boolean(msg.adminId);
              return (
                <div
                  key={msg._id}
                  className={`flex ${
                    isAdmin ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[75%] ${isAdmin ? "order-2" : "order-1"}`}
                  >
                    {!isAdmin && (
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white">
                          <span className="text-sm font-medium">C</span>
                        </div>
                      </div>
                    )}
                    <div
                      className={`rounded-2xl px-4 py-2 ${
                        isAdmin
                          ? "bg-green-100 text-gray-800 rounded-br-none"
                          : "bg-gray-200 text-gray-800 rounded-bl-none"
                      }`}
                    >
                      <p className="text-sm">{msg.message}</p>
                    </div>
                    <p
                      className={`text-xs text-gray-500 mt-1 ${
                        isAdmin ? "text-right" : "text-left"
                      }`}
                    >
                      {formatTime(msg.createdAt)}
                    </p>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2 mb-2 order-1 mr-2">
                      <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white">
                        <span className="text-sm font-medium">A</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t bg-white">
          <div className="flex items-center gap-2">
            <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <BsCameraFill className="w-5 h-5 text-gray-600" />
            </button>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Write a comment"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              disabled={isSending}
            />
            <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <MdInsertEmoticon className="w-6 h-6 text-gray-600" />
            </button>
            <button
              onClick={sendMessage}
              disabled={isSending || !newMessage.trim()}
              className={`p-2 rounded-full transition-colors ${
                isSending || !newMessage.trim()
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              }`}
            >
              <IoSend className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default OrderSupportChatModal;
